"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import eventMarketersGraphic from "@/app/(public)/shared/assets/graphics/event-marketers.png";
import { CheckCircle } from "lucide-react";

export default function EventMarketers() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">The smartest way to plan events that convert.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Whether you're hosting a thought leadership summit or a VIP dinner, Nowadays helps you create events
                that drive results.
              </p>
              <div className="flex gap-3">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Get Started
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={eventMarketersGraphic}
                alt="Event marketers solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">
              Your brand deserves better than a ballroom. Let's make it unforgettable.
            </h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Create standout experiences that capture leads, build brand awareness, and drive conversions.
            </p>
          </div>

          {/* Core Features - converted to document style */}
          <div className="space-y-12 mb-20">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Source standout venues with wow-factor</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Access unique venues that create memorable experiences and align with your brand. Move beyond generic
                hotel ballrooms to distinctive spaces that reflect your company's personality and leave lasting
                impressions on attendees.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Automate quotes, A/V, and F&B logistics</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Streamline event planning with automated workflows for all your logistical needs. Our integrated
                platform connects you with trusted vendors and automates the quote process, freeing you to focus on
                creative strategy and attendee experience.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Track ROI with budget breakdowns and attendee flows</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Measure event success with comprehensive analytics and ROI tracking. Understand which touchpoints drive
                the most engagement, monitor budget allocation in real-time, and demonstrate clear value to stakeholders
                with detailed performance metrics.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Launch branded registration pages in minutes</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Create professional registration experiences that match your brand identity. Customize forms, integrate
                with your marketing stack, and provide seamless signup experiences that convert prospects into attendees
                while capturing valuable lead data.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Integrated marketing campaign management</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Seamlessly integrate events into your broader marketing strategy with tools that connect to your CRM,
                email platforms, and social media channels for comprehensive campaign tracking. Build cohesive campaigns
                that align your events with your broader marketing objectives and maximize your reach.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Lead generation and qualification</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Capture and qualify leads at every touchpoint with intelligent forms, engagement tracking, and automated
                follow-up sequences that convert attendees into customers. Transform event participation into measurable
                pipeline growth with sophisticated lead nurturing workflows.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Brand experience optimization</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Create consistent, on-brand experiences across all your events with customizable templates, brand
                guidelines integration, and real-time feedback collection. Ensure every touchpoint reinforces your brand
                identity and delivers memorable experiences that resonate with your audience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" type="general" featuredIndex={0} title="Don't just take our word for it." />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Event Marketing Solutions"
        subtitle="Schedule a call to learn how we can amplify your brand"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your marketing demo"
      />
    </main>
  );
}
